import { useState, useEffect } from "react";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { ref, get } from "firebase/database";
import { db } from "../../../configs/Firebase";
import { calculateStepScore, getAggregatedStepScore } from "../utils/scoringUtils";
import style from "./ScoringSummaryDialog.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";
import DialogNew from "../../../designSystem/c-organism/dialogs/DialogNew";

const DIMENSION_LABELS = {
  taskClarity: "Task Clarity",
  cognitiveLoad: "Cognitive Load",
  errorPrevention: "Error Prevention & Recovery",
  consistency: "Consistency",
  efficiency: "Efficiency",
  feedback: "Feedback & System Status",
};

const ScoringSummaryDialog = ({
  show,
  onClose,
  evaluations,
  aggregatedScore,
  pictureURL,
}) => {
  const [evaluationsWithProfiles, setEvaluationsWithProfiles] = useState([]);

  useEffect(() => {
    if (!show || !evaluations || evaluations.length === 0) {
      setEvaluationsWithProfiles([]);
      return;
    }

    // Fetch profile data for each designer
    const fetchProfiles = async () => {
      const profilesPromises = evaluations.map(async (evaluation) => {
        try {
          const profileRef = ref(db, `userData/${evaluation.designerId}/profile`);
          const profileSnapshot = await get(profileRef);
          const profile = profileSnapshot.val() || {};

          return {
            ...evaluation,
            designerDisplayName: evaluation.designerName || profile.displayName || evaluation.designerId,
            designerEmail: profile.email || "",
            designerPhotoURL: profile.microsoftPhotoURL || null,
            designerInitials: evaluation.designerName
              ? evaluation.designerName.split(" ")[0].charAt(0) +
              (evaluation.designerName.split(" ")[1]
                ? evaluation.designerName.split(" ")[1].charAt(0)
                : "")
              : profile.email
                ? profile.email.charAt(0).toUpperCase()
                : "?",
          };
        } catch (error) {
          console.error(`Error fetching profile for ${evaluation.designerId}:`, error);
          return {
            ...evaluation,
            designerDisplayName: evaluation.designerName || evaluation.designerId,
            designerEmail: "",
            designerPhotoURL: null,
            designerInitials: evaluation.designerName
              ? evaluation.designerName.charAt(0).toUpperCase()
              : "?",
          };
        }
      });

      const profiles = await Promise.all(profilesPromises);
      setEvaluationsWithProfiles(profiles);
    };

    fetchProfiles();
  }, [show, evaluations]);

  function getScoreColor(score) {
    if (score === null || score === undefined || score === "N/A") return "neutral";
    const numScore = Number(score);
    if (numScore >= 4) return "high";
    if (numScore >= 3) return "medium";
    return "low";
  }

  function formatDate(timestamp) {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const stepScoreFinal = aggregatedScore?.stepScoreFinalPercentage || null;
  const variance = aggregatedScore?.stepScoreVariance || 0;
  const hasHighVariance = aggregatedScore?.hasHighVariance || false;

  return (
    <DialogNew show={show} onClose={onClose} variants={["large"]} title="Evaluation Summary" actions={[{ name: "Close", onClick: onClose }]}>

      <div className={style.dialogContentContainer}>
        {pictureURL && (
          <div className={style.pictureReference}>
            <img src={pictureURL} alt="Step reference" className={style.referenceImage} />
          </div>
        )}
        <div className={style.dialogContent}>


          {/* Aggregated Summary */}
          <div className={style.summarySection}>
            <h3 className={style.summaryTitle}>Aggregated Scores</h3>
            <div className={style.summaryMetrics}>
              <div className={style.metric}>
                <span className={style.metricLabel}>Overall Score</span>
                <span className={style.metricValue}>
                  {stepScoreFinal !== null ? `${Math.round(stepScoreFinal)}%` : "N/A"}
                </span>
              </div>
              <div className={style.metric}>
                <span className={style.metricLabel}>Variance</span>
                <span className={style.metricValue}>
                  {variance.toFixed(2)}
                  {hasHighVariance && (
                    <span className={style.varianceIcon}>
                      <Icon>warning</Icon>
                    </span>
                  )}
                </span>
              </div>
              <div className={style.metric}>
                <span className={style.metricLabel}>Evaluations</span>
                <span className={style.metricValue}>{evaluations?.length || 0}</span>
              </div>
            </div>
          </div>

          {/* Individual Evaluations */}
          <div className={style.evaluationsSection}>
            <h3 className={style.evaluationsTitle}>Designer Evaluations</h3>
            {evaluationsWithProfiles.length > 0 ? (
              <div className={style.evaluationsList}>
                {evaluationsWithProfiles.map((evaluation) => {
                  const stepScore = calculateStepScore(evaluation);
                  const stepScorePercentage = stepScore !== null ? (stepScore / 5) * 100 : null;

                  return (
                    <div key={evaluation.id} className={style.evaluationCard}>
                      <div className={style.evaluationHeader}>
                        <div className={style.designerInfo}>
                          <div
                            className={style.designerAvatar}
                            style={
                              evaluation.designerPhotoURL
                                ? {
                                  backgroundImage: `url(${evaluation.designerPhotoURL})`,
                                  backgroundSize: "cover",
                                  backgroundPosition: "center",
                                  color: "transparent",
                                }
                                : {}
                            }
                          >
                            {!evaluation.designerPhotoURL && evaluation.designerInitials}
                          </div>
                          <div className={style.designerDetails}>
                            <div className={style.designerName}>
                              {evaluation.designerDisplayName}
                            </div>
                            {evaluation.designerEmail && (
                              <div className={style.designerEmail}>
                                {evaluation.designerEmail}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={style.evaluationScore}>
                          <span className={style.scoreLabel}>Step Score</span>
                          <span className={style.scoreValue}>
                            {stepScorePercentage !== null
                              ? `${Math.round(stepScorePercentage)}%`
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className={style.dimensionsGrid}>
                        {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
                          const score = evaluation.dimensions?.[key];
                          const scoreValue = score === "N/A" ? "N/A" : Number(score);
                          return (
                            <div key={key} className={style.dimensionScore}>
                              <span className={style.dimensionLabel}>{label}</span>
                              <span
                                className={`${style.scoreBadge} ${style[getScoreColor(score)]}`}
                              >
                                {scoreValue}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {evaluation.updatedAt && (
                        <div className={style.evaluationDate}>
                          Updated: {formatDate(evaluation.updatedAt)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={style.noEvaluations}>
                No evaluations yet. Click to evaluate in edit mode.
              </div>
            )}
          </div>
        </div>
      </div>

    </DialogNew>
  );
};

export default ScoringSummaryDialog;

